import { type IAppointmentCreditState } from '@principle-theorem/ng-principle-shared';
import {
  AccountCredit,
  getBaseTransaction,
} from '@principle-theorem/principle-core';
import {
  type IAccountCredit,
  type IAccountCreditExtendedData,
  type IAppointment,
  type IInvoice,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  snapshot,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { sum } from 'lodash';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AccountCreditTransactionBuilder } from './account-credit-transaction-builder';

export type AccountCreditTransactionRef = DocumentReference<
  ITransaction<IAccountCreditExtendedData>
>;

export interface IAppointmentCreditCaptureRequest {
  invoice: WithRef<IInvoice>;
  appointmentRef: DocumentReference<IAppointment>;
  /**
   * Subscribed to when the capture runs so the credits are re-read from
   * Firestore at that point rather than taken from an earlier snapshot.
   */
  credits$: Observable<WithRef<IAccountCredit>[]>;
}

/**
 * Owns the credit-state and transaction decisions that let an appointment
 * consume only its eligible booking deposits without duplicating capture rules.
 */
export class AppointmentCreditCapture {
  /**
   * Calculates one remaining-credit total so state and payment affordances use
   * the same balance definition.
   */
  static totalRemaining(credits: WithRef<IAccountCredit>[]): number {
    return sum(credits.map((credit) => AccountCredit.remaining(credit)));
  }

  /**
   * Derives the available balance from live credits so payment UI updates with
   * the same calculation used by capture.
   */
  static totalAvailableCredit$(
    credits$: Observable<WithRef<IAccountCredit>[]>
  ): Observable<number> {
    return credits$.pipe(map(AppointmentCreditCapture.totalRemaining));
  }

  /**
   * Drops exhausted credits and classifies the remainder so appointment-specific
   * capture never consumes a credit that cannot contribute to its balance.
   */
  static toAppointmentCreditState(
    credits: WithRef<IAccountCredit>[],
    appointmentRef?: DocumentReference<IAppointment>
  ): IAppointmentCreditState {
    const availableCredits = credits.filter(
      (credit) => AccountCredit.remaining(credit) > 0
    );
    const classification = AccountCredit.classifyForAppointment(
      availableCredits,
      appointmentRef
    );
    return {
      ...classification,
      matchingTotal: AppointmentCreditCapture.totalRemaining(
        classification.matching
      ),
      unreservedTotal: AppointmentCreditCapture.totalRemaining(
        classification.unreserved
      ),
      // The three buckets partition `availableCredits`, so this is the same
      // balance `totalAvailableCredit$` reports to the Account Credit payment
      // option — by construction, from the same helper and the same credits.
      availableTotal: AppointmentCreditCapture.totalRemaining(availableCredits),
    };
  }

  /**
   * Emits appointment-ready credit state from live credits so callers do not
   * duplicate classification across capture and display paths.
   */
  static appointmentCreditState$(
    credits$: Observable<WithRef<IAccountCredit>[]>,
    appointmentRef?: DocumentReference<IAppointment>
  ): Observable<IAppointmentCreditState> {
    return credits$.pipe(
      map((credits) =>
        AppointmentCreditCapture.toAppointmentCreditState(
          credits,
          appointmentRef
        )
      )
    );
  }

  /**
   * Builds a balance-capped booking-deposit transaction without persisting it,
   * so the caller owns the single path that writes and reports the capture.
   */
  static async buildAppointmentCreditCapture(
    request: IAppointmentCreditCaptureRequest
  ): Promise<ITransaction<IAccountCreditExtendedData> | undefined> {
    const credits = await snapshot(request.credits$);
    const { matching, matchingTotal } =
      AppointmentCreditCapture.toAppointmentCreditState(
        credits,
        request.appointmentRef
      );
    if (!matching.length || matchingTotal <= 0) {
      return;
    }

    const baseTransaction = await getBaseTransaction(request.invoice);
    const amount = Math.min(matchingTotal, baseTransaction.amount);
    if (amount <= 0) {
      return;
    }

    const { transaction } =
      AccountCreditTransactionBuilder.buildAccountCreditTransaction({
        baseTransaction,
        credits: matching,
        amount,
        practiceRef: request.invoice.practice.ref,
      });
    return transaction;
  }
}
